import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, set, push, onValue } from "firebase/database";
import { getAuth } from 'firebase/auth';
import DashboardHeader from '../../AdminComponents/DashboardHeader';
import EmailSenderSettings from '../Components/EmailSenderSettings';
import ProfessionalEmailBuilder from '../Components/ProfessionalEmailBuilder';
import './EmailDesigner.css';

const EmailDesigner = () => {
    const { templateId } = useParams();
    const navigate = useNavigate();
    
    const [templateData, setTemplateData] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [templateDescription, setTemplateDescription] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [emailSenders, setEmailSenders] = useState([]);
    const [showSenderSettings, setShowSenderSettings] = useState(false);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const isNewTemplate = templateId === 'new';

    // Load email senders
    useEffect(() => {
        if (currentUser) {
            const sendersRef = ref(db, `emailSenders/${currentUser.uid}`);
            onValue(sendersRef, (snapshot) => {
                if (snapshot.exists()) {
                    const sendersData = [];
                    snapshot.forEach((childSnapshot) => {
                        sendersData.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });
                    setEmailSenders(sendersData);
                } else {
                    setEmailSenders([]);
                }
            });
        }
    }, [currentUser, db]);

    // Load existing template if editing
    useEffect(() => {
        if (!isNewTemplate && templateId && currentUser) {
            const templateRef = ref(db, `emailTemplates/${templateId}`);
            get(templateRef).then((snapshot) => {
                if (snapshot.exists()) {
                    const template = snapshot.val();
                    if (template.userId === currentUser.uid) {
                        setTemplateData(template);
                        setTemplateName(template.name || '');
                        setTemplateDescription(template.description || '');
                    } else {
                        alert('You do not have permission to edit this template.');
                        navigate('/emailflow');
                    }
                } else {
                    alert('Template not found.');
                    navigate('/emailflow');
                }
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [templateId, isNewTemplate, currentUser, db, navigate]);

    // Check for senders before allowing template creation
    useEffect(() => {
        if (!loading && emailSenders.length === 0 && isNewTemplate && !showSenderSettings) {
            alert('Bitte erstellen Sie zuerst einen Email-Absender, bevor Sie Templates erstellen können.');
            setShowSenderSettings(true);
        }
    }, [loading, emailSenders, isNewTemplate, showSenderSettings]);

    const handleSaveTemplate = async (templateContent) => {
        if (!templateName.trim()) {
            alert('Please enter a template name');
            return;
        }

        setSaving(true);
        try {
            const templateDataToSave = {
                userId: currentUser.uid,
                name: templateName.trim(),
                description: templateDescription.trim(),
                subject: templateContent.subject || templateName.trim(),
                html: templateContent.html,
                design: templateContent.design, // React Email structure
                mergeTags: templateContent.mergeTags || [],
                createdAt: isNewTemplate ? Date.now() : templateData?.createdAt,
                updatedAt: Date.now()
            };

            if (isNewTemplate) {
                // Create new template
                const newTemplateRef = push(ref(db, 'emailTemplates'));
                await set(newTemplateRef, templateDataToSave);
                alert('Template saved successfully!');
                navigate('/emailflow');
            } else {
                // Update existing template
                await set(ref(db, `emailTemplates/${templateId}`), templateDataToSave);
                alert('Template updated successfully!');
                navigate('/emailflow');
            }
        } catch (error) {
            console.error('Error saving template:', error);
            alert('Error saving template. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-spinner">
                <h1>BonusClub</h1>
                <div className="spinner-circle"></div>
                <p>Loading email template...</p>
            </div>
        );
    }

    // Show sender settings if no senders configured
    if (emailSenders.length === 0 && isNewTemplate) {
        return (
            <div>
                <DashboardHeader />
                <EmailSenderSettings 
                    onClose={() => navigate('/emailflow')}
                    onSenderSelected={() => setShowSenderSettings(false)}
                />
            </div>
        );
    }

    return (
        <div className="email-designer-container">
            <DashboardHeader />
            
            <div className="email-designer-header">
                <div className="email-designer-info">
                    <input
                        type="text"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Enter template name..."
                        className="email-template-name-input"
                    />
                    <input
                        type="text"
                        value={templateDescription}
                        onChange={(e) => setTemplateDescription(e.target.value)}
                        placeholder="Template description..."
                        className="email-template-description-input"
                    />
                </div>
                
                <div className="email-designer-actions">
                    <button 
                        className="email-designer-btn close"
                        onClick={() => navigate('/emailflow')}
                    >
                        ← Back to Email Flow
                    </button>
                </div>
            </div>

            <div className="email-designer-main">
                <ProfessionalEmailBuilder 
                    onSave={handleSaveTemplate}
                    initialTemplate={templateData?.design || null}
                />
            </div>

            {showSenderSettings && (
                <EmailSenderSettings 
                    onClose={() => {
                        setShowSenderSettings(false);
                        if (emailSenders.length === 0 && isNewTemplate) {
                            navigate('/emailflow');
                        }
                    }}
                    onSenderSelected={() => {
                        setShowSenderSettings(false);
                    }}
                />
            )}
        </div>
    );
};

export default EmailDesigner;